import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  BellOff,
  Download,
  Info,
  Package,
  Plus,
  Send,
  ShieldAlert,
  Upload,
  X,
  Clock,
  CheckCircle2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { statsApi } from "@/lib/stats-api";
import { api } from "@/lib/api";
import {
  ensureNotificationPermission,
  startReminderScheduler,
  stopReminderScheduler,
} from "@/lib/notifications";

const MAX_REMINDERS = 3;

/**
 * Settings modal — currently one section: notification reminders.
 *
 * Behaviour:
 *   - Toggle "Enable reminders" requests browser Notification permission
 *     the first time it flips on. Denied → toast + toggle stays off.
 *   - Up to 3 HH:MM slots (Dhaka). Add via "+ time" or edit any row.
 *   - Save persists to /api/settings and (re)starts the JS scheduler.
 *   - Honest limitation copy: "Works while the app or browser is open".
 */
export default function SettingsModal({ open, onOpenChange, onSaved, onCleared }) {
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState(["10:00", "15:00", "20:00"]);
  // Feb-2026 random daily targets. Default TRUE (matches server default)
  // so a freshly opened modal shows the toggle in its intended state
  // even before the GET resolves.
  const [randomTargets, setRandomTargets] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState("default");
  // Telegram bot info (username, deep link). null = not yet fetched,
  // { enabled: false } = disabled server-side, { unreachable: true } = down.
  const [telegram, setTelegram] = useState(null);
  // Danger zone state — the "clear month" flow is two-step:
  //   1. click "Clear this month's data" opens the confirm dialog
  //   2. user must type CLEAR to enable the destructive Confirm button
  const [clearOpen, setClearOpen] = useState(false);
  const [clearInput, setClearInput] = useState("");
  const [clearing, setClearing] = useState(false);
  const CLEAR_PHRASE = "CLEAR";

  // Backup / restore state.
  //   - backupBusy / restoreBusy: block the buttons while the request
  //     is in flight so a stray double-click can't fire two restores.
  //   - restoreFile + restoreOpen: pending-file-then-confirm flow so
  //     the destructive operation requires an explicit second click.
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const restoreInputRef = useRef(null);

  // Self-host zip builder state (Feb-2026). Each variant has its own
  // in-flight guard so the user can click both buttons back-to-back
  // without them stepping on each other; the backend serialises builds
  // via a lock anyway. `info` holds the response of /selfhost/status.
  const [selfhostBusy, setSelfhostBusy] = useState({ clean: false, with_env: false });
  const [selfhostInfo, setSelfhostInfo] = useState(null);

  // Telegram Reports state ------------------------------------------
  // Config is loaded whenever the modal opens so the user sees the
  // latest `last_sent_at` and `last_error`.
  const [tgCfg, setTgCfg] = useState(null);
  const [tgLoading, setTgLoading] = useState(false);
  const [tgEnabled, setTgEnabled] = useState(false);
  const [tgBotToken, setTgBotToken] = useState("");
  const [tgChatId, setTgChatId] = useState("");
  const [tgSending, setTgSending] = useState(false);
  const [tgDirty, setTgDirty] = useState(false);

  // --- IP Checker API keys (Feb-2026) ------------------------------
  // Server returns {has_value, hint} per slot so the UI can show
  // "set · …1234" without leaking the full secret. The five string
  // inputs below hold user-typed-but-not-yet-saved values; empty
  // means "no change" (null when sent). An explicit "Clear" button
  // sends an empty string to wipe the slot.
  // Batch #21: trimmed to 5 slots (scamalytics / ipqs / ipregistry /
  // iplocate / abuseipdb). Previous ipdata.co + ip2location.io slots
  // were retired along with their providers.
  const [ipcheckKeys, setIpcheckKeys] = useState({
    scamalytics_api_key: { has_value: false, hint: "" },
    ipqs_api_key:        { has_value: false, hint: "" },
    ipregistry_api_key:  { has_value: false, hint: "" },
    iplocate_api_key:    { has_value: false, hint: "" },
    abuseipdb_api_key:   { has_value: false, hint: "" },
  });
  const [ipcheckKeyInputs, setIpcheckKeyInputs] = useState({
    scamalytics_api_key: "",
    ipqs_api_key: "",
    ipregistry_api_key: "",
    iplocate_api_key: "",
    abuseipdb_api_key: "",
  });
  // Live provider quota (today's calls per provider, loaded from
  // /ipcheck/providers so the Settings modal doesn't need its own
  // separate endpoint).
  const [providerQuota, setProviderQuota] = useState({ providers: [], quota_date: null });
  const ipcheckSectionRef = useRef(null);
  const clearMonth = useMemo(() => {
    // Compute Dhaka YYYY-MM on the client purely for the copy — the
    // authoritative month is calculated server-side.
    try {
      const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Dhaka",
        year: "numeric",
        month: "2-digit",
      });
      return fmt.format(new Date()).slice(0, 7);
    } catch {
      return new Date().toISOString().slice(0, 7);
    }
  }, [clearOpen]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    } else {
      setPermission("unsupported");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    statsApi
      .getSettings()
      .then((s) => {
        if (cancelled) return;
        setEnabled(!!s.reminders_enabled);
        setTimes(
          Array.isArray(s.reminder_times) && s.reminder_times.length > 0
            ? s.reminder_times.slice(0, MAX_REMINDERS)
            : ["10:00", "15:00", "20:00"],
        );
        // Legacy servers without the field surface as `undefined`; the
        // spec says default to ON in that case.
        setRandomTargets(
          s.random_targets_enabled === undefined
            ? true
            : !!s.random_targets_enabled,
        );
        // IP Checker key slots (Feb-2026). Older server versions won't
        // ship the field — fall back to all-empty so the UI still
        // renders the inputs for the user to populate.
        if (s.ipcheck_keys && typeof s.ipcheck_keys === "object") {
          setIpcheckKeys(s.ipcheck_keys);
        }
        setIpcheckKeyInputs({
          scamalytics_api_key: "",
          ipqs_api_key: "",
          ipregistry_api_key: "",
          iplocate_api_key: "",
          abuseipdb_api_key: "",
        });
      })
      .catch(() => {
        /* keep defaults */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Fire-and-forget: fetch Telegram info in parallel. This never blocks
    // the main settings load — it just decorates the modal when ready.
    statsApi
      .telegramInfo()
      .then((info) => {
        if (!cancelled) setTelegram(info || { enabled: false });
      })
      .catch(() => {
        if (!cancelled) setTelegram({ enabled: true, unreachable: true });
      });
    // Fetch today's provider quota so each IP Checker key row can
    // render "N calls today". Non-fatal — missing data just hides the
    // chip.
    api.ipcheck
      .providers()
      .then((d) => {
        if (!cancelled) setProviderQuota(d || { providers: [], quota_date: null });
      })
      .catch(() => {
        if (!cancelled) setProviderQuota({ providers: [], quota_date: null });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const canSave = useMemo(
    () => times.every((t) => /^\d{1,2}:\d{2}$/.test(t)),
    [times],
  );

  const handleToggle = async (value) => {
    if (!value) {
      setEnabled(false);
      return;
    }
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    const perm = await ensureNotificationPermission();
    setPermission(perm);
    if (perm !== "granted") {
      toast.error(
        perm === "denied"
          ? "Notifications blocked — enable them in your browser settings."
          : "Notification permission wasn't granted.",
      );
      setEnabled(false);
      return;
    }
    setEnabled(true);
  };

  const updateTime = (idx, v) => {
    setTimes((arr) => arr.map((t, i) => (i === idx ? v : t)));
  };

  const removeTime = (idx) => {
    setTimes((arr) => arr.filter((_, i) => i !== idx));
  };

  const addTime = () => {
    setTimes((arr) => (arr.length >= MAX_REMINDERS ? arr : [...arr, "12:00"]));
  };

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Fix the time format (HH:MM)");
      return;
    }
    setSaving(true);
    try {
      // Only include an ipcheck key field when the input is non-empty
      // or the user explicitly cleared a previously-set slot. "null"
      // (field absent) means "don't touch".
      const keyUpdates = {};
      for (const slot of [
        "scamalytics_api_key",
        "ipqs_api_key",
        "ipregistry_api_key",
        "iplocate_api_key",
        "abuseipdb_api_key",
      ]) {
        const typed = (ipcheckKeyInputs[slot] || "").trim();
        if (typed) keyUpdates[slot] = typed;
      }
      const saved = await statsApi.putSettings({
        reminders_enabled: enabled,
        reminder_times: times,
        random_targets_enabled: randomTargets,
        ...keyUpdates,
      });
      // Re-arm the scheduler based on saved values (server may normalise).
      if (saved.reminders_enabled) {
        startReminderScheduler({
          enabled: true,
          times: saved.reminder_times,
        });
        toast.success("Reminders enabled");
      } else {
        stopReminderScheduler();
        toast.message("Reminders off");
      }
      // Refresh the key hints from the save response so the UI shows
      // the new masked value immediately (and clear the input fields).
      if (saved.ipcheck_keys) setIpcheckKeys(saved.ipcheck_keys);
      setIpcheckKeyInputs({
        scamalytics_api_key: "",
        ipqs_api_key: "",
        ipregistry_api_key: "",
        iplocate_api_key: "",
        abuseipdb_api_key: "",
      });
      onSaved && onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg ||
        e?.response?.data?.detail ||
        "Save failed — try again.";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Clear a specific IP-checker key slot without saving the whole form
  // (sends an empty string to the backend; backend treats "" as "clear").
  const handleClearIpcheckKey = async (slot) => {
    try {
      const saved = await statsApi.putSettings({
        reminders_enabled: enabled,
        reminder_times: times,
        random_targets_enabled: randomTargets,
        [slot]: "",
      });
      if (saved.ipcheck_keys) setIpcheckKeys(saved.ipcheck_keys);
      setIpcheckKeyInputs((prev) => ({ ...prev, [slot]: "" }));
      toast.success("Key cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  // Scroll the IP Checker section into view whenever Settings opens
  // via the "needs key" hint. The parent sets `location.hash` to
  // `#ipcheck-keys` right before opening so we can honour it here.
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#ipcheck-keys") return;
    // Wait two ticks for the Dialog's internal scroll container to
    // mount, then scroll the section into view. Radix Dialog wraps
    // our content in a scrollable div — scrollIntoView bubbles up to
    // the first scrollable ancestor, which is what we want.
    const t = setTimeout(() => {
      const el = ipcheckSectionRef.current;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Fallback — force the nearest scrollable ancestor to scroll
        // in case the browser's native scrollIntoView refuses inside
        // a `fixed`-positioned Dialog on Safari/iOS.
        requestAnimationFrame(() => {
          try {
            let node = el.parentElement;
            while (node) {
              const style = getComputedStyle(node);
              const scrollable = /(auto|scroll)/.test(style.overflowY + style.overflow);
              if (scrollable && node.scrollHeight > node.clientHeight) {
                node.scrollTo({ top: el.offsetTop - node.offsetTop - 16, behavior: "smooth" });
                break;
              }
              node = node.parentElement;
            }
          } catch { /* ignore */ }
        });
      }
      try {
        history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        /* ignore history API restrictions on file:// */
      }
    }, 220);
    return () => clearTimeout(t);
  }, [open]);

  const handleClearMonth = async () => {
    if (clearInput.trim().toUpperCase() !== CLEAR_PHRASE) return;
    setClearing(true);
    try {
      const res = await statsApi.clearMonth();
      toast.success(
        `Cleared ${clearMonth}: ` +
          `${res?.deleted?.daily_counts ?? 0} click rows, ` +
          `${res?.deleted?.earnings ?? 0} earnings.`,
      );
      setClearOpen(false);
      setClearInput("");
      onCleared && onCleared(res);
      onOpenChange(false);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Clear failed — try again. No rows were deleted.";
      toast.error(typeof msg === "string" ? msg : "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  // --- Backup / Restore handlers ---------------------------------
  // Backup downloads a JSON file; we create a transient <a> element so
  // the browser pops its native Save-As dialog. The URL is revoked
  // right after click to avoid holding the Blob in memory.
  // --- Self-host zip builder (Phase 8) ---------------------------
  // One click → GET /api/selfhost/generate → the backend builds a
  // fresh zip against the current /app source and streams it back.
  // We trigger via window.location.href so the browser handles the
  // download natively (progress bar, Save-As dialog). A fetch+blob
  // round-trip would also work but you'd buffer 31 MB in memory for
  // no gain. The with_env variant needs the opaque token exposed by
  // /selfhost/status.
  const loadSelfhostStatus = async () => {
    try {
      const s = await statsApi.selfhostStatus();
      setSelfhostInfo(s);
    } catch {
      /* non-fatal — UI just hides timestamps if the probe failed */
    }
  };

  const handleSelfhostDownload = (variant) => {
    if (selfhostBusy[variant]) return;
    setSelfhostBusy((prev) => ({ ...prev, [variant]: true }));
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const url = `${base}/api/selfhost/generate?variant=${encodeURIComponent(variant)}`;
    // Kick the browser download. window.location.assign is more reliable
    // than a programmatic <a>.click() across mobile Safari + Firefox.
    window.location.assign(url);
    // The server builds in ~2 s and streams the 31 MB over the next few
    // seconds; relax the busy flag after a generous grace period so the
    // button comes back usable even if the user cancels the save dialog.
    setTimeout(() => {
      setSelfhostBusy((prev) => ({ ...prev, [variant]: false }));
      loadSelfhostStatus();
    }, 10_000);
  };

  // --- Backup / Restore handlers ---------------------------------
  // Backup downloads a JSON file; we create a transient <a> element so
  // the browser pops its native Save-As dialog. The URL is revoked
  // right after click to avoid holding the Blob in memory.
  const handleBackup = async () => {
    setBackupBusy(true);
    try {
      const { blob, filename } = await api.maintenance.backup();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally {
      setBackupBusy(false);
    }
  };

  // Restore is TWO clicks: pick a file (fires onChange) then confirm.
  const handlePickRestore = (file) => {
    if (!file) return;
    setRestoreFile(file);
    setRestoreOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!restoreFile) return;
    setRestoreBusy(true);
    try {
      const res = await api.maintenance.restore(restoreFile);
      const counts = res?.restored || {};
      const summary = Object.entries(counts)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(" · ");
      toast.success(`Restored — ${summary || "no rows"}`);
      setRestoreOpen(false);
      setRestoreFile(null);
      if (restoreInputRef.current) restoreInputRef.current.value = "";
      onCleared && onCleared({ restored: counts });
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Restore failed";
      toast.error(typeof msg === "string" ? msg : "Restore failed");
    } finally {
      setRestoreBusy(false);
    }
  };

  // --- Sync status (2-way Mongo ↔ Supabase mirror) ----------------
  // Lightweight read of /api/sync/status. Non-blocking — the modal
  // still opens / renders even if the sync endpoint is unreachable.
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const loadSyncStatus = async () => {
    setSyncLoading(true);
    try {
      const s = await statsApi.getSyncStatus();
      setSyncStatus(s);
    } catch {
      setSyncStatus({ health: "red", _unreachable: true });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await statsApi.syncReconcile();
      const pushed = res?.actions?.pushed;
      const pulled = res?.actions?.pulled;
      if (pushed && pushed.only?.length) {
        toast.success(`Reconciled · pushed ${pushed.only.join(", ")}`);
      } else if (pulled) {
        toast.success("Reconciled · pulled from Supabase");
      } else {
        toast.success("Stores already in sync");
      }
      await loadSyncStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    loadSyncStatus();
    loadSelfhostStatus();
    // Poll every 20s while the modal is open so the user sees the
    // outbox drain / Supabase recover in near-real-time.
    const t = setInterval(loadSyncStatus, 20_000);
    return () => clearInterval(t);
  }, [open]);

  // --- Telegram Reports handlers ---------------------------------
  const loadTgConfig = async () => {
    setTgLoading(true);
    try {
      const cfg = await api.telegramReports.getConfig();
      setTgCfg(cfg);
      setTgEnabled(!!cfg.enabled);
      setTgChatId(cfg.chat_id || "");
      // Leave bot_token blank — we never pre-fill it. The masked value
      // is shown as a placeholder for context only.
      setTgBotToken("");
      setTgDirty(false);
    } catch (e) {
      // Silent — this surface is optional.
    } finally {
      setTgLoading(false);
    }
  };

  // Load config whenever the dialog opens.
  useEffect(() => {
    if (open) loadTgConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTgSave = async () => {
    try {
      const patch = { enabled: tgEnabled };
      // Only send non-empty fields so clearing the input doesn't
      // accidentally wipe the saved token.
      if (tgBotToken.trim()) patch.bot_token = tgBotToken.trim();
      if (tgChatId.trim() !== (tgCfg?.chat_id || "")) patch.chat_id = tgChatId.trim();
      const next = await api.telegramReports.patchConfig(patch);
      setTgCfg(next);
      setTgEnabled(!!next.enabled);
      setTgBotToken("");
      setTgDirty(false);
      toast.success("Telegram report settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const handleTgSendNow = async () => {
    setTgSending(true);
    try {
      const res = await api.telegramReports.sendNow();
      if (res?.ok) {
        toast.success(`Sent — message #${res.message_id}`);
      } else {
        toast.error(res?.error || "Send failed");
      }
      await loadTgConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setTgSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-modal"
        className="max-h-[92vh] max-w-[92vw] overflow-y-auto border-white/10 bg-[#0a0a0c] text-text-main sm:max-w-md"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-lg font-bold tracking-tight">
            Settings
          </DialogTitle>
          <DialogDescription className="font-mono-safe text-[11px] text-text-muted">
            Notification reminders (Asia/Dhaka).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-sm text-text-muted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Random daily targets toggle (Feb-2026) */}
            <div
              className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
              data-testid="random-targets-row"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className={
                    "flex h-9 w-9 items-center justify-center rounded-lg " +
                    (randomTargets
                      ? "border border-accent/40 bg-accent/15 text-accent"
                      : "border border-white/10 bg-white/[0.03] text-text-muted")
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    {/* dice-3 icon — evokes randomness w/o an emoji */}
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <circle cx="8" cy="8" r="1" />
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="16" cy="16" r="1" />
                  </svg>
                </span>
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold text-text-main">
                    Random daily targets
                  </span>
                  <span className="font-mono-safe text-[11px] text-text-muted">
                    {randomTargets
                      ? "On · rolls a fresh target every Dhaka day"
                      : "Off · uses each link's manual target"}
                  </span>
                </div>
              </div>
              <Switch
                data-testid="random-targets-toggle"
                checked={randomTargets}
                onCheckedChange={setRandomTargets}
              />
            </div>

            {/* --- 2-way sync status --------------------------------
                Compact read-only card. Color-coded dot + counts +
                outbox-pending. Hidden entirely when Supabase isn't
                configured on this pod (self-host without the mirror).
                Poll runs every 20 s while the modal is open. */}
            {syncStatus && !syncStatus._unreachable && syncStatus.supabase_configured !== false && (
              <div
                className="rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
                data-testid="sync-status-card"
                data-sync-health={syncStatus.health || "unknown"}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden
                      data-testid="sync-health-dot"
                      className={
                        "h-2.5 w-2.5 rounded-full " +
                        (syncStatus.health === "green"
                          ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.75)]"
                          : syncStatus.health === "amber"
                            ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.75)]"
                            : "bg-rose-400 shadow-[0_0_6px_rgba(251,113,133,0.75)]")
                      }
                    />
                    <span className="text-sm font-semibold text-text-main">
                      Sync {syncStatus.health === "green" ? "healthy" : syncStatus.health === "amber" ? "catching up" : "offline"}
                    </span>
                  </div>
                  {syncLoading && (
                    <span className="font-mono-safe text-[10px] text-text-muted">
                      refreshing…
                    </span>
                  )}
                </div>
                {/* Error / down-for banner — only renders when the
                    mirror is actually in trouble. Deliberately above
                    the counts grid so the user reads WHY the numbers
                    look wrong before parsing them. */}
                {(syncStatus.supabase_error || (syncStatus.pool?.fail_streak || 0) > 0) && (
                  <div
                    className={
                      "mb-2 rounded border px-2 py-1.5 font-mono-safe text-[11px] " +
                      (syncStatus.health === "red"
                        ? "border-rose-500/35 bg-rose-500/10 text-rose-100"
                        : "border-amber-500/35 bg-amber-500/10 text-amber-100")
                    }
                    data-testid="sync-error-banner"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">
                        {syncStatus.health === "red"
                          ? "Supabase unreachable"
                          : "Supabase connection flaky"}
                      </span>
                      {(syncStatus.pool?.down_for_seconds || 0) > 60 && (
                        <span className="text-[10px] opacity-80">
                          down for{" "}
                          {syncStatus.pool.down_for_seconds >= 3600
                            ? `${Math.floor(syncStatus.pool.down_for_seconds / 3600)}h ${Math.floor((syncStatus.pool.down_for_seconds % 3600) / 60)}m`
                            : `${Math.round(syncStatus.pool.down_for_seconds / 60)}m`}
                        </span>
                      )}
                    </div>
                    {syncStatus.supabase_error && (
                      <div className="mt-0.5 truncate text-[10.5px] opacity-85" title={syncStatus.supabase_error}>
                        {syncStatus.supabase_error}
                      </div>
                    )}
                    {(syncStatus.pool?.fail_streak || 0) > 0 && (
                      <div className="mt-0.5 text-[10px] opacity-70">
                        retrying with exponential backoff
                        {(syncStatus.pool?.cooldown_remaining_s || 0) > 0
                          ? ` · next in ${Math.round(syncStatus.pool.cooldown_remaining_s)}s`
                          : ""}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono-safe text-[11px] text-text-muted">
                  <div>
                    Mongo links:{" "}
                    <span data-testid="sync-mongo-links" className="text-text-main">
                      {syncStatus.mongo?.links ?? 0}
                    </span>
                  </div>
                  <div>
                    Supabase links:{" "}
                    <span data-testid="sync-supa-links" className="text-text-main">
                      {syncStatus.supabase?.links ?? 0}
                    </span>
                  </div>
                  <div>
                    Daily counts:{" "}
                    <span className="text-text-main">
                      {syncStatus.mongo?.daily_counts ?? 0}
                      {" / "}
                      {syncStatus.supabase?.daily_counts ?? 0}
                    </span>
                  </div>
                  <div>
                    Earnings:{" "}
                    <span className="text-text-main">
                      {syncStatus.mongo?.earnings ?? 0}
                      {" / "}
                      {syncStatus.supabase?.earnings ?? 0}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center justify-between pt-1">
                    <span>
                      Outbox pending:{" "}
                      <span
                        data-testid="sync-outbox-pending"
                        className={
                          (syncStatus.outbox_pending || 0) > 0
                            ? "text-amber-300"
                            : "text-text-main"
                        }
                      >
                        {syncStatus.outbox_pending ?? 0}
                      </span>
                    </span>
                    {syncStatus.last_mirror_success && (
                      <span className="text-text-muted/80">
                        last ok{" "}
                        {new Date(
                          syncStatus.last_mirror_success,
                        ).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
                {/* Reconcile-now action. Hidden entirely when stores
                    already match (health=green AND outbox=0) — no point
                    showing a button that would just no-op. */}
                {(syncStatus.health !== "green" || (syncStatus.outbox_pending || 0) > 0) && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleReconcile}
                      disabled={reconciling}
                      data-testid="sync-reconcile-now"
                      className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent/25 disabled:opacity-50"
                    >
                      {reconciling ? "Reconciling…" : "Reconcile now"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Enable toggle */}
            <div
              className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
              data-testid="reminders-row"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className={
                    "flex h-9 w-9 items-center justify-center rounded-lg " +
                    (enabled
                      ? "border border-accent/40 bg-accent/15 text-accent"
                      : "border border-white/10 bg-white/[0.03] text-text-muted")
                  }
                >
                  {enabled ? (
                    <Bell className="h-4 w-4" />
                  ) : (
                    <BellOff className="h-4 w-4" />
                  )}
                </span>
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold text-text-main">
                    Enable reminders
                  </span>
                  <span className="font-mono-safe text-[11px] text-text-muted">
                    {enabled ? "On" : "Off"}
                    {permission === "denied" && " · blocked in browser"}
                  </span>
                </div>
              </div>
              <Switch
                data-testid="reminders-toggle"
                checked={enabled}
                onCheckedChange={handleToggle}
              />
            </div>

            {/* Reminder times */}
            <div
              className="rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
              data-testid="reminder-times"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
                  Times (24h Dhaka)
                </span>
                <span className="font-mono-num text-[11px] text-text-muted">
                  {times.length}/{MAX_REMINDERS}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {times.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2"
                    data-testid={`reminder-time-${i}`}
                  >
                    <Input
                      value={t}
                      onChange={(e) => updateTime(i, e.target.value)}
                      placeholder="HH:MM"
                      inputMode="numeric"
                      pattern="[0-2]?[0-9]:[0-5][0-9]"
                      maxLength={5}
                      data-testid={`reminder-time-input-${i}`}
                      disabled={!enabled}
                      className="h-9 flex-1 border-white/10 bg-black/30 font-mono-num text-text-main disabled:opacity-50 focus-visible:border-accent focus-visible:ring-accent/50"
                    />
                    <button
                      type="button"
                      onClick={() => removeTime(i)}
                      disabled={!enabled}
                      data-testid={`reminder-time-remove-${i}`}
                      aria-label="Remove time"
                      className="focus-accent flex h-9 w-9 items-center justify-center rounded-md text-text-muted hover:bg-red-500/10 hover:text-red-300 disabled:opacity-30"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {times.length < MAX_REMINDERS && (
                  <button
                    type="button"
                    onClick={addTime}
                    disabled={!enabled}
                    data-testid="reminder-time-add"
                    className="focus-accent inline-flex items-center gap-1.5 self-start rounded-full border border-dashed border-white/15 px-3 py-1 text-xs text-text-muted transition-colors hover:border-accent/40 hover:text-accent disabled:opacity-30"
                  >
                    <Plus className="h-3 w-3" />
                    Add time
                  </button>
                )}
              </div>
            </div>

            {/* Telegram block (Phase 4) — shows the bot username + link
                when the backend is wired up. Nudges the user to prefer
                Telegram over browser notifications since Telegram
                reminders keep firing when the browser is closed. */}
            {telegram && telegram.enabled && (
              <div
                className="rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
                data-testid="telegram-block"
              >
                <div className="mb-2 flex items-center gap-3">
                  <span
                    aria-hidden
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-accent/30 bg-accent/10 text-accent"
                  >
                    <Send className="h-4 w-4" />
                  </span>
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold text-text-main">
                      Telegram bot
                    </span>
                    <span
                      className="font-mono-safe text-[11px] text-text-muted"
                      data-testid="telegram-username"
                    >
                      {telegram.unreachable
                        ? "Bot is configured but unreachable right now."
                        : telegram.username
                          ? `@${telegram.username}`
                          : "Configured"}
                    </span>
                  </div>
                </div>
                {telegram.link && !telegram.unreachable && (
                  <a
                    href={telegram.link}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="telegram-open-link"
                    className="focus-accent inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold text-accent transition-colors hover:bg-accent/25"
                  >
                    Open in Telegram →
                  </a>
                )}
                <p className="mt-2 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                  Send <code className="text-text-main">/start</code> to the
                  bot to receive reminders on the schedule above.
                  Telegram reminders keep working even when this browser
                  is closed — unlike web notifications.
                </p>
              </div>
            )}

            {/* Honest limitation copy */}
            <div
              className="flex items-start gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 font-mono-safe text-[11px] leading-relaxed text-text-muted"
              data-testid="reminders-limitation"
            >
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted/70" />
              <span>
                Works while the app or browser is open. RefClick has no push
                server, so notifications can&apos;t fire when the browser is
                fully closed. Reminders skip automatically when today&apos;s
                targets are already met.
              </span>
            </div>

            {/* ─────────── Telegram weekly reports ─────────── */}
            <div
              className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid="telegram-report-section"
            >
              <div className="mb-2 flex items-center gap-2">
                <Send className="h-3.5 w-3.5 shrink-0 text-text-main" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-main">
                  Telegram weekly report
                </span>
                <label className="ml-auto flex items-center gap-2 text-[11px] text-text-main">
                  <input
                    type="checkbox"
                    checked={tgEnabled}
                    onChange={(e) => { setTgEnabled(e.target.checked); setTgDirty(true); }}
                    className="h-3.5 w-3.5 accent-accent"
                    data-testid="tg-report-enabled"
                  />
                  Enabled
                </label>
              </div>
              <p className="mb-2 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                Sends a zip (full backup.json + progress.md) to your
                Telegram chat once a week. Credentials are stored in
                the database so they survive redeploys.
              </p>

              <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="block text-[11px] text-text-main">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-text-muted">
                    Bot token
                  </span>
                  <input
                    type="password"
                    value={tgBotToken}
                    onChange={(e) => { setTgBotToken(e.target.value); setTgDirty(true); }}
                    placeholder={tgCfg?.bot_token_masked || "paste bot token"}
                    className="focus-accent w-full rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[11px] text-text-main placeholder:text-text-muted"
                    data-testid="tg-report-token"
                    autoComplete="off"
                  />
                </label>
                <label className="block text-[11px] text-text-main">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-text-muted">
                    Chat ID
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={tgChatId}
                    onChange={(e) => { setTgChatId(e.target.value); setTgDirty(true); }}
                    placeholder="123456789"
                    className="focus-accent w-full rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[11px] text-text-main placeholder:text-text-muted"
                    data-testid="tg-report-chat"
                  />
                </label>
              </div>

              {/* Status line */}
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px]">
                {tgCfg?.last_sent_at ? (
                  <span className="inline-flex items-center gap-1 text-emerald-300" data-testid="tg-report-last-sent">
                    <CheckCircle2 className="h-3 w-3" />
                    Last sent:{" "}
                    <span className="font-mono">
                      {new Date(tgCfg.last_sent_at).toLocaleString(undefined, {
                        year: "numeric", month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit",
                      })}
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-text-muted">
                    <Clock className="h-3 w-3" />
                    Never sent — a report will go out on next tick when enabled.
                  </span>
                )}
                {tgCfg?.last_error && (
                  <span className="inline-flex max-w-full items-center gap-1 truncate text-rose-300" data-testid="tg-report-last-error" title={tgCfg.last_error}>
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    {tgCfg.last_error}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleTgSave}
                  disabled={!tgDirty}
                  data-testid="tg-report-save"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-text-main transition-colors hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleTgSendNow}
                  disabled={tgSending || !tgCfg?.bot_token_set || !tgCfg?.chat_id}
                  data-testid="tg-report-send-now"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-3 w-3" />
                  {tgSending ? "Sending…" : "Send test report now"}
                </button>
                {tgLoading && (
                  <span className="text-[10px] text-text-muted">Loading…</span>
                )}
              </div>
            </div>

            {/* Backup & Restore — right above the danger zone so it's
                easy to grab a full dump before a destructive op. */}
            <div
              className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid="backup-restore-section"
            >
              <div className="mb-2 flex items-center gap-2">
                <Download className="h-3.5 w-3.5 shrink-0 text-text-main" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-main">
                  Backup &amp; restore
                </span>
              </div>
              <p className="mb-3 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                Download a single JSON file with every collection
                (links, clicks, earnings, settings, IP history, legacy
                data, watchlist). Restore from that same file to
                recover after an accident. Idempotent — re-restoring the
                same file is safe.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleBackup}
                  disabled={backupBusy}
                  data-testid="backup-download"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-text-main transition-colors hover:bg-white/[0.1] disabled:opacity-60"
                >
                  <Download className="h-3 w-3" />
                  {backupBusy ? "Preparing…" : "Download full backup"}
                </button>
                <input
                  ref={restoreInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => handlePickRestore(e.target.files?.[0])}
                  data-testid="restore-file-input"
                />
                <button
                  type="button"
                  onClick={() => restoreInputRef.current?.click()}
                  disabled={restoreBusy}
                  data-testid="restore-open"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 transition-colors hover:bg-amber-500/20 disabled:opacity-60"
                >
                  <Upload className="h-3 w-3" />
                  Restore from backup…
                </button>
              </div>
            </div>

            {/* Self-host package — on-demand zip builder. Replaces the
                old pre-built static downloads so every click reflects
                the current code at that moment. */}
            <div
              className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid="selfhost-section"
            >
              <div className="mb-2 flex items-center gap-2">
                <Package className="h-3.5 w-3.5 shrink-0 text-text-main" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-main">
                  Self-host package
                </span>
              </div>
              <p className="mb-3 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                Build a fresh zip of the current code — ready to run on
                any Docker host. Clean = source only (safe to share).
                With my config = source plus the LIVE
                <span className="font-semibold text-amber-200"> .env secrets</span>
                 (Telegram token, Supabase DSN) for a one-command deploy.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSelfhostDownload("clean")}
                  disabled={selfhostBusy.clean}
                  data-testid="selfhost-clean-btn"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-text-main transition-colors hover:bg-white/[0.1] disabled:opacity-60"
                >
                  <Download className="h-3 w-3" />
                  {selfhostBusy.clean ? "Building…" : "Generate clean zip"}
                </button>
                <button
                  type="button"
                  onClick={() => handleSelfhostDownload("with_env")}
                  disabled={selfhostBusy.with_env}
                  data-testid="selfhost-withenv-btn"
                  className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 transition-colors hover:bg-amber-500/20 disabled:opacity-60"
                >
                  <ShieldAlert className="h-3 w-3" />
                  {selfhostBusy.with_env
                    ? "Building…"
                    : "Generate zip with my config"}
                </button>
              </div>
              {(selfhostInfo?.clean || selfhostInfo?.with_env) && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono-safe text-[10px] text-text-muted/80"
                  data-testid="selfhost-last-built"
                >
                  {selfhostInfo?.clean && (
                    <span>
                      Clean:{" "}
                      <span className="text-text-main">
                        {(selfhostInfo.clean.size_bytes / 1024 / 1024).toFixed(1)}&nbsp;MB
                      </span>
                      ·{" "}
                      {new Date(selfhostInfo.clean.built_at).toLocaleTimeString()}
                    </span>
                  )}
                  {selfhostInfo?.with_env && (
                    <span>
                      With config:{" "}
                      <span className="text-text-main">
                        {(selfhostInfo.with_env.size_bytes / 1024 / 1024).toFixed(1)}&nbsp;MB
                      </span>
                      ·{" "}
                      {new Date(selfhostInfo.with_env.built_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* IP Checker API keys — paid-provider slots. Keys are
                kept server-side in the settings doc and surfaced back
                as masked hints. Batch #21 — now a 5-slot lineup
                (Scamalytics, IPQS, Ipregistry, IPLocate.io, AbuseIPDB)
                after retiring proxycheck / ipdata / ip2location. */}
            <div
              ref={ipcheckSectionRef}
              id="ipcheck-keys"
              className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid="ipcheck-keys-section"
            >
              <div className="mb-2 flex items-center gap-2">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-text-main" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-main">
                  IP Checker · API keys
                </span>
              </div>
              <p className="mb-3 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                Add paid-provider keys to unlock the full fraud score.
                Tier-2 providers (IPLocate / Ipregistry / AbuseIPDB /
                Scamalytics) are called only when the free-tier score
                ≥ 30; IPQualityScore (tier 3) runs only when ≥ 60 so
                its tight quota lasts longer. Keys live server-side,
                never surfaced back in plain text. Leave blank to keep
                the current value; use <strong>Clear</strong> to remove.
              </p>
              <div className="space-y-2">
                {[
                  { slot: "scamalytics_api_key", name: "Scamalytics",    url: "https://scamalytics.com/ip/api",                         tier: 2 },
                  { slot: "ipqs_api_key",        name: "IPQualityScore", url: "https://www.ipqualityscore.com/create-account",         tier: 3 },
                  { slot: "ipregistry_api_key",  name: "Ipregistry",     url: "https://ipregistry.co/sign-up",                          tier: 2 },
                  { slot: "iplocate_api_key",    name: "IPLocate.io",    url: "https://iplocate.io/signup",                             tier: 2 },
                  { slot: "abuseipdb_api_key",   name: "AbuseIPDB",      url: "https://www.abuseipdb.com/account/api",                  tier: 2 },
                ].map(({ slot, name, url, tier }) => {
                  const info = ipcheckKeys?.[slot] || { has_value: false, hint: "" };
                  // Map slot → provider name used by the quota endpoint
                  // so we can show "X calls today" per row.
                  const providerName = slot.replace("_api_key", "");
                  const quotaRow = (providerQuota.providers || []).find(
                    (p) => p.name === providerName,
                  );
                  const callsToday = quotaRow?.calls_today ?? 0;
                  return (
                    <div
                      key={slot}
                      className="flex flex-wrap items-center gap-2"
                      data-testid={`ipcheck-key-row-${slot}`}
                    >
                      <div className="flex min-w-[112px] shrink-0 items-center gap-1 text-[11px] font-semibold text-text-main">
                        <span>{name}</span>
                        <span
                          className="rounded-sm bg-white/10 px-1 font-mono text-[9px] text-text-muted"
                          title={`Tier ${tier} — ${
                            tier === 3 ? "runs when score ≥ 60"
                                       : "runs when score ≥ 30"
                          }`}
                        >
                          T{tier}
                        </span>
                      </div>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        spellCheck={false}
                        value={ipcheckKeyInputs[slot]}
                        onChange={(e) =>
                          setIpcheckKeyInputs((prev) => ({ ...prev, [slot]: e.target.value }))
                        }
                        placeholder={info.has_value ? "•••• (saved)" : "paste key here"}
                        className="h-7 flex-1 min-w-[140px] bg-white/[0.04] font-mono text-[11px]"
                        data-testid={`ipcheck-key-input-${slot}`}
                      />
                      <span
                        className="shrink-0 rounded-sm bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-text-muted"
                        title={`Calls today (Dhaka ${providerQuota.quota_date || "—"})`}
                        data-testid={`ipcheck-quota-${slot}`}
                      >
                        {callsToday} today
                      </span>
                      {info.has_value ? (
                        <div className="flex shrink-0 items-center gap-1">
                          <span
                            className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 font-mono text-[10px] text-emerald-300"
                            title={`Current key ends in ${info.hint}`}
                            data-testid={`ipcheck-key-hint-${slot}`}
                          >
                            set · {info.hint || "✓"}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleClearIpcheckKey(slot)}
                            className="focus-accent text-[10.5px] text-rose-300 underline-offset-2 hover:underline"
                            data-testid={`ipcheck-key-clear-${slot}`}
                          >
                            Clear
                          </button>
                        </div>
                      ) : (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="focus-accent shrink-0 text-[10.5px] text-text-muted underline-offset-2 hover:text-accent hover:underline"
                          data-testid={`ipcheck-key-signup-${slot}`}
                        >
                          Get a key →
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Danger zone — clear this Dhaka month's clicks + earnings. */}
            <div
              className="rounded-lg border border-red-500/30 bg-red-500/[0.04] px-4 py-3"
              data-testid="danger-zone"
            >
              <div className="mb-2 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-red-300">
                  Danger zone
                </span>
              </div>
              <p className="mb-3 font-mono-safe text-[11px] leading-relaxed text-text-muted">
                Erase every click and every $ earning dated in the current
                Dhaka month{" "}
                <span className="font-mono-num text-text-main">
                  ({clearMonth})
                </span>{" "}
                across all links. Previous months are untouched. Your links,
                custom fields, and settings stay exactly as they are.
              </p>
              <button
                type="button"
                onClick={() => {
                  setClearInput("");
                  setClearOpen(true);
                }}
                data-testid="danger-clear-month-open"
                className="focus-accent inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 transition-colors hover:bg-red-500/20"
              >
                <AlertTriangle className="h-3 w-3" />
                Clear this month&apos;s data
              </button>
            </div>
          </div>
        )}

        <div className="mt-1 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="settings-cancel"
            className="text-text-muted hover:text-text-main"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !canSave}
            data-testid="settings-save"
            className="border border-accent/40 bg-accent/20 text-accent hover:bg-accent/30"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>

      {/* Danger-zone confirmation — nested so the parent Settings dialog
          stays open behind it. Requires typing CLEAR to enable the
          destructive button (two-step guard). */}
      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent
          data-testid="danger-clear-month-confirm"
          className="border-red-500/25 bg-[#101012] text-text-main"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Clear this month&apos;s data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-text-muted">
              <span className="block">
                This will erase this month&apos;s clicks and $ amounts for
                <strong className="text-text-main"> all links</strong>. Past
                months stay. This cannot be undone.
              </span>
              <span className="block font-mono-safe text-[11px]">
                Month:{" "}
                <span className="font-mono-num text-text-main">
                  {clearMonth}
                </span>
              </span>
              <span className="block pt-1 text-[11px]">
                Type{" "}
                <code className="rounded bg-white/5 px-1 py-0.5 font-mono-num text-red-300">
                  CLEAR
                </code>{" "}
                below to enable the button.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={clearInput}
            onChange={(e) => setClearInput(e.target.value)}
            placeholder="Type CLEAR"
            data-testid="danger-clear-month-input"
            autoComplete="off"
            spellCheck={false}
            disabled={clearing}
            className="h-9 border-red-500/30 bg-black/40 font-mono-num text-text-main placeholder:text-text-muted/40 focus-visible:border-red-400 focus-visible:ring-red-500/40"
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={clearing}
              data-testid="danger-clear-month-cancel"
              className="border-white/10 bg-white/[0.02] text-text-main hover:bg-white/[0.06]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearMonth}
              disabled={
                clearing ||
                clearInput.trim().toUpperCase() !== CLEAR_PHRASE
              }
              data-testid="danger-clear-month-confirm-btn"
              className="border border-red-500/40 bg-red-500/25 text-red-100 hover:bg-red-500/35 disabled:opacity-40"
            >
              {clearing ? "Clearing…" : "Yes, clear this month"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore-from-backup confirmation. Destructive: upserts every
          row from the uploaded JSON. Second click gate. */}
      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent
          data-testid="restore-confirm"
          className="border-amber-500/25 bg-[#101012] text-text-main"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <Upload className="h-4 w-4 text-amber-300" />
              Restore from backup?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-text-muted">
              <span className="block">
                This will upsert every row from{" "}
                <span className="font-mono text-text-main">
                  {restoreFile?.name || "the selected file"}
                </span>{" "}
                into Mongo. Rows with the same ID will be overwritten;
                rows that are not in the backup are left alone.
              </span>
              <span className="block">
                Idempotent — running it twice is safe.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="restore-cancel"
              onClick={() => {
                setRestoreFile(null);
                if (restoreInputRef.current) restoreInputRef.current.value = "";
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="restore-confirm-btn"
              disabled={restoreBusy}
              onClick={handleConfirmRestore}
              className="border-amber-500/40 bg-amber-500/80 text-black hover:bg-amber-400"
            >
              {restoreBusy ? "Restoring…" : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
